import { Chat } from '@/shared/interfaces/Chat'
import {Message, MessageGroup} from '@/shared/interfaces/Message'
import { StoreBase } from '@/store/base/StoreBase'
import { chatsLoad } from './Methods/chatsLoad'
import { TimeFormat } from '@/shared/helpers/TimeFormat'
import {Contact} from '@/shared/interfaces/Contact'

export interface ChatsStoreState {
  isLoading: boolean;

  userId: number;
  chats: Chat[];

  chatsLoadPromise?: Promise<Chat[]>;

  selectedChat: Chat | null;
  selectedProfile: Contact | null;

  messageGroups: MessageGroup[];
  messagesPage: number;
  areMessagesLoading: boolean;

  areMoreMessagesLoading: boolean;
  areAllMessagesLoaded: boolean;

  isUnreadFilterEnabled: boolean;
  messagesCache: Map<number, ChatMessagesCache>;
}

export interface ChatMessagesCache {
  messageGroups: MessageGroup[];
  messagesPage: number;
  areAllMessagesLoaded: boolean;
  areMessagesLoading: boolean;
  areMoreMessagesLoading: boolean;
}

export class ChatsStore extends StoreBase<ChatsStoreState> {
  public static getNamespace(): string {
    return 'chats'
  }

  constructor() {
    super(ChatsStore.getNamespace())
  }

  public static get(): {
    chatsStore: ChatsStore;
    chatsState: ChatsStoreState;
  } {
    const namespace = this.getNamespace()

    if (!this._hasModule(namespace)) {
      this._register<ChatsStoreState>(namespace, {
        isLoading: true,

        userId: 0,

        chats: [],
        selectedChat: null,
        selectedProfile: null,
        messageGroups: [],
        messagesPage: 1,
        areMessagesLoading: true,

        areMoreMessagesLoading: false,
        areAllMessagesLoaded: false,

        isUnreadFilterEnabled: false,
        messagesCache: new Map<number, ChatMessagesCache>(),
      })
      setTimeout(async () => {
        await chatsLoad()
      }, 0)
    }

    const store = new ChatsStore()

    return {
      chatsStore: store,
      chatsState: store.state,
    }
  }

  public cacheCurrentChatMessages(): void {
    const { selectedChat, messageGroups, messagesPage, areAllMessagesLoaded, areMessagesLoading, areMoreMessagesLoading } = this.state
    if (!selectedChat) return

    this.state.messagesCache.set(selectedChat.id, {
      messageGroups: JSON.parse(JSON.stringify(messageGroups)),
      messagesPage,
      areAllMessagesLoaded,
      areMessagesLoading,
      areMoreMessagesLoading,
    })
  }

  public restoreMessagesFromCache(chatId: number): boolean {
    const cached = this.state.messagesCache.get(chatId)
    if (!cached) return false

    this.state.messageGroups = cached.messageGroups
    this.state.messagesPage = cached.messagesPage
    this.state.areAllMessagesLoaded = cached.areAllMessagesLoaded
    this.state.areMessagesLoading = cached.areMessagesLoading
    this.state.areMoreMessagesLoading = cached.areMoreMessagesLoading
    return true
  }

  public invalidateCacheForChat(chatId: number): void {
    this.state.messagesCache.delete(chatId)
  }

  public clearMessagesCache(): void {
    this.state.messagesCache.clear()
  }

  public updateCacheForChat(chatId: number, updater: (cached: ChatMessagesCache) => void): void {
    const cached = this.state.messagesCache.get(chatId)
    if (cached) {
      updater(cached)
      this.state.messagesCache.set(chatId, cached)
    }
  }



  public async addMessageToCache(chatId: number, message: Message) {
    let cached = this.state.messagesCache.get(chatId)
    if (!cached) {
      cached = {
        messageGroups: [],
        messagesPage: 1,
        areAllMessagesLoaded: false,
        areMessagesLoading: false,
        areMoreMessagesLoading: false,
      }
      this.state.messagesCache.set(chatId, cached)
    }

    const messageDate = TimeFormat.dayStart(message.dt_departure)
    let targetGroup = cached.messageGroups.find(g => g.date === messageDate)

    if (!targetGroup) {
      targetGroup = { messages: [], date: messageDate }
      cached.messageGroups = [targetGroup, ...cached.messageGroups]
    }

    if (!targetGroup.messages.some(m => m.id === message.id)) {
      targetGroup.messages = [message, ...targetGroup.messages]
    }

    this.state.messagesCache.set(chatId, cached)
  }

}
